<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />

   <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->

   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>

   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">

                  <!-- Table action row (shared: TableToolbar.vue). No Add: audit logs are written by the system only -->
         <TableToolbar
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :can-delete="false"
            @filter="openFilters"
            @clear-selection="clearSelection"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />


         <!-- Table -->
         <div class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('actor_user_id')" column="actor_user_id" :table-data="tableData" @sort="orderBy">User</SortableTh>
         <SortableTh v-if="isColumnVisible('action_type')" column="action_type" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Action Type</SortableTh>
         <SortableTh v-if="isColumnVisible('table_name')" column="table_name" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Table</SortableTh>
         <SortableTh v-if="isColumnVisible('record_id')" column="record_id" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Record ID</SortableTh>
         <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">
         <!-- Main row -->
         <tr class="clickable-row"
             :class="{ 'active-row main-row': tableData.selectedRecordId === record.id }"
             @click="$onRowClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('actor_user_id')">
               <!-- Phones: + / − and the number go in front of the user (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>{{ record.actor_name || record.actor_user_id }}
            </td>
            <td v-if="isColumnVisible('action_type')" class="hide-sm-td">{{ record.action_type }}</td>
            <td v-if="isColumnVisible('table_name')" class="hide-sm-td">{{ record.table_name }}</td>
            <td v-if="isColumnVisible('record_id')" class="hide-sm-td">{{ record.record_id }}</td>
            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
            <td>
               <div class="d-flex justify-content-end align-items-center w-100 flex-wrap">
                  <div class="btn-group">
                     <!-- Computers: "Actions ▾". Phones: ⋮ only (app-table.css: .row-actions-btn) -->
                     <button type="button" class="btn btn-secondary dropdown-toggle row-actions-btn" data-bs-toggle="dropdown" aria-label="Actions">
                        <i class="fa fa-ellipsis-v d-md-none"></i><span class="d-none d-md-inline">Actions</span>
                     </button>
                     <ul class="dropdown-menu dropdown-menu-end actions-menu">                        
                        <li>
                           <button type="button" class="dropdown-item" @click="$toggleRecordDetails(record.id)">
                              <i class="fa fa-eye me-1 text-info"></i> View
                           </button>
                        </li>
                        <!-- Only when the action was saved with a position -->
                        <li v-if="mapUrl(record)">
                           <a class="dropdown-item" :href="mapUrl(record)" target="_blank" rel="noopener noreferrer">
                              <i class="fa fa-map-marker me-1 text-danger"></i> View on map
                           </a>
                        </li>
                     </ul>
                  </div>
               </div>

            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="7" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>Log Number:</strong> {{ record.audit_log_number }}</div>
                     <div><i class="fa fa-user"></i><strong>Performed By:</strong> {{ record.actor_name || record.actor_user_id }}</div>
                     <div><i class="fa fa-building"></i><strong>Company Name:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Branch Name:</strong> {{ record.branch_name }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('action_type') }"><i class="fa fa-bolt"></i><strong>Action Type:</strong> {{ record.action_type }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('table_name') }"><i class="fa fa-table"></i><strong>Table:</strong> {{ record.table_name }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('record_id') }"><i class="fa fa-key"></i><strong>Record ID:</strong> {{ record.record_id }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>

                     <!-- Always in details -->
                     <div><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                     <div>
                        <i class="fa fa-map-marker"></i><strong>Location:</strong> {{ record.current_location }}
                        <a v-if="mapUrl(record)" :href="mapUrl(record)" target="_blank" rel="noopener noreferrer" class="ms-1">
                           View on map <i class="fa fa-external-link small"></i>
                        </a>
                     </div>
                     <div v-if="mapUrl(record)"><i class="fa fa-crosshairs"></i><strong>Coordinates:</strong> {{ record.latitude }}, {{ record.longitude }}</div>
                     <div><i class="fa fa-plug"></i><strong>Source:</strong> {{ record.source }}</div>
                     <div><i class="fa fa-globe"></i><strong>User Agent:</strong> {{ record.user_agent }}</div>

                     <div>
                        <i class="fa fa-history"></i><strong>Old Data:</strong>
                        <pre class="mb-0 small audit-json">{{ formatJson(record.old_data) }}</pre>
                     </div>
                     <div>
                        <i class="fa fa-file-text"></i><strong>New Data:</strong>
                        <pre class="mb-0 small audit-json">{{ formatJson(record.new_data) }}</pre>
                     </div>
                  </div>
               </div>
            </td>
         </tr>
      </template>

      <!-- No rows: blank while loading, then the empty message in the middle (no Add: the system writes the logs) -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="9" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No activity yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Actions people take in the app are recorded here.'"
               icon="fa fa-history"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="9">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>

         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import auditLogsMixin from "./mixins/auditLogsMixin.js";
import tableMixin from "@/mixins/tableMixin.js";


export default {
   name: 'AuditLogs',
   mixins: [tableMixin, auditLogsMixin], // shared table methods first, then AuditLogs' own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      SearchBox,
      Pagination,
      TableEmptyState,
      SortableTh,
   },
   data() {
      return {
         // Standard data (tableData, defaultAlert ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up the table shortcuts
         apiUrl: "/logs/api_audit_logs.php", // used by tableMixin's fetchRecords, export
         exportFileName: "auditLogsExport", // name of the exported file
         columnsKey: "auditlogs",          // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         pageIcon: 'fa fa-history', // same icon as this page in the sidebar
         // Filter window: View (logs are never deleted, so no "Deleted")
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "Action Type", value: "action_type" },
            { label: "Table Name", value: "table_name" },
            { label: "Record ID", value: "record_id" },
            { label: "IP Address", value: "ip_address" },
            { label: "Source", value: "source" },
         ],
      };
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
      // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Audit Logs",
              byme: "Audit Logs By Me",
              deletedone: "Audit Logs Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Audit Logs";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "actor_user_id", label: "User" },
              { key: "action_type", label: "Action Type" },
              { key: "table_name", label: "Table" },
              { key: "record_id", label: "Record ID" },
              { key: "date_created", label: "Date" },
          ];
      },
  },
      methods: {
      getDefaultFormInputs(){
         return { id: "" };
      },
      // Google Maps link to the exact spot of the action, or null when it has no position
      mapUrl(record) {
         if (record.latitude === null || record.latitude === undefined || record.longitude === null || record.longitude === undefined) {
            return null;
         }
         return `https://www.google.com/maps/search/?api=1&query=${record.latitude},${record.longitude}`;
      },
      // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'action_type',
            searchPlaceHolderText: 'Search by Action Type',
         };
      },
   },
}; 
</script>